// Orion product-support surface: a header icon that opens a right-side blade
// embedding the Orion portal in an iframe. Mirrors ServiceAI's "Ask Orion"
// constellation toggle (ServiceAI.Client/Layout/MainLayout.razor) — here the
// blade frames the configured portal rather than hosting an AI chat, since the
// control plane has no chat backend. The portal base URL comes from the
// regional `orion.portalUrl` system setting via GET /api/config/orion-portal.

import { useEffect, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import type { OrionPortalConfig } from '@shared/api-types';
import { apiFetch } from '../lib/apiClient';
import { Blade } from './Blade';

// Compact constellation glyph, retranscribed from ServiceAI's
// `orion-constellation-icon` SVG. Self-contained colours (not currentColor) so
// the disc reads the same in light/dark themes, matching ServiceAI's look.
// Gradient ids are namespaced to avoid clashing with other inline SVGs.
function OrionGlyph({ size = 22 }: { size?: number }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
      <defs>
        <radialGradient id="orion-hdr-bg" cx="50%" cy="45%" r="70%">
          <stop offset="0%" stopColor="#3a35a0" />
          <stop offset="100%" stopColor="#0a0a30" />
        </radialGradient>
        <radialGradient id="orion-hdr-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="40%" stopColor="#cfd6ff" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#7aa6ff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#orion-hdr-bg)" />
      <g fill="#ffffff" opacity="0.5">
        <circle cx="20" cy="22" r="0.8" />
        <circle cx="78" cy="18" r="0.7" />
        <circle cx="84" cy="40" r="0.9" />
        <circle cx="16" cy="62" r="0.7" />
        <circle cx="34" cy="80" r="0.8" />
        <circle cx="68" cy="84" r="0.7" />
        <circle cx="88" cy="68" r="0.6" />
      </g>
      <circle cx="50" cy="50" r="32" fill="url(#orion-hdr-glow)" />
      <path d="M 50 18 Q 52 48 80 50 Q 52 52 50 82 Q 48 52 20 50 Q 48 48 50 18 Z" fill="#ffffff" />
      <circle cx="50" cy="50" r="47.5" fill="none" stroke="#a78bff" strokeWidth="0.6" opacity="0.5" />
    </svg>
  );
}

// Load state: undefined = not fetched yet, null = configured-but-absent / error,
// string = portal base URL.
type PortalState = string | null | undefined;

export function OrionSupport() {
  const [open, setOpen] = useState(false);
  const [portalUrl, setPortalUrl] = useState<PortalState>(undefined);

  // Fetch lazily the first time the blade is opened; cache for the session.
  useEffect(() => {
    if (!open || portalUrl !== undefined) return;
    let cancelled = false;
    apiFetch<OrionPortalConfig>('/api/config/orion-portal')
      .then((c) => {
        if (!cancelled) setPortalUrl(c.portalUrl ?? null);
      })
      .catch(() => {
        if (!cancelled) setPortalUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, portalUrl]);

  const body =
    portalUrl === undefined ? (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader size="large" type="converging-spinner" />
      </div>
    ) : portalUrl === null ? (
      <div style={{ color: 'var(--fg-muted)', fontSize: 14, lineHeight: 1.5 }}>
        Product support isn’t configured yet. Once an administrator sets the Orion
        portal URL, it’ll appear here.
      </div>
    ) : (
      <iframe
        title="Orion product support"
        src={portalUrl}
        style={{ width: '100%', height: '100%', border: 0, borderRadius: 6, background: 'var(--bg)' }}
      />
    );

  return (
    <>
      <button
        type="button"
        className="header-icon-btn orion-support-btn"
        aria-label="Product support"
        title="Product support"
        onClick={() => setOpen(true)}
      >
        {/* Disc ring around the constellation glyph — mirrors ServiceAI's
            .orion-constellation-disc (a 2px currentColor ring that glows on
            hover). See .orion-disc in AppShell.css. */}
        <span className="orion-disc">
          <OrionGlyph size={24} />
        </span>
      </button>
      {open && (
        <Blade
          title="Product Support"
          subtitle="Powered by ServiceAI"
          icon={<OrionGlyph size={24} />}
          width="min(720px, 96vw)"
          onClose={() => setOpen(false)}
          footer={
            portalUrl ? (
              <Button
                fillMode="flat"
                onClick={() => window.open(portalUrl, '_blank', 'noopener,noreferrer')}
              >
                Open in new tab ↗
              </Button>
            ) : undefined
          }
        >
          {body}
        </Blade>
      )}
    </>
  );
}

export default OrionSupport;
